import styles from './EditModeContent.module.scss';

import { Button } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { inlineMentions } from '../../../../../../input/format-text/TextEditor/plugins/mention/utils';
import { textEditorCommentKeydown } from '../../../utils/text-editor-comment-keydown';
import { CommentEditor } from '../../CommentEditor';
import { useEditCommentMutation, useProcessHtml } from '../hooks';

import { ViewContentProps } from './ViewContent';

type EditModeContentProps = Pick<
  ViewContentProps,
  'event' | 'onEditFinished' | 'canAddOrInsertImage'
>;

export const EditModeContent = ({
  event,
  onEditFinished,
  canAddOrInsertImage,
}: EditModeContentProps) => {
  const { t } = useTranslation();
  const html = useProcessHtml(event.text);

  const [comment, setComment] = useState(html);

  const [editComment, mutationResult] = useEditCommentMutation({
    onCompleted() {
      onEditFinished();
    },
  });

  const handleEditComment = async (htmlContent?: string) => {
    if (mutationResult.loading) return;

    await editComment({
      variables: {
        codeOrId: event.id,
        objectType: event.objectType,
        comment: inlineMentions(!htmlContent ? comment : htmlContent),
      },
    });
  };

  return (
    <div className={styles.container}>
      <CommentEditor
        value={comment}
        setValue={(value) => {
          setComment(value);
        }}
        canAddOrInsertImage={canAddOrInsertImage}
        editorConf={{
          placeholderText: '',
          height: 52,
          heightMin: 150,
          heightMax: 300,
          multiLine: false,
          events: {
            keydown: function (e: KeyboardEvent) {
              textEditorCommentKeydown(e, {
                edit: () => {
                  const htmlContent = this.html.get();
                  if (htmlContent && !mutationResult.loading) {
                    handleEditComment(htmlContent);
                  }
                },
                newLine: () => {
                  this.cursor.enter();
                },
              });
            },
          },
          toolbarButtons,
          toolbarButtonsXS: toolbarButtons,
          toolbarButtonsSM: toolbarButtons,
          toolbarButtonsMD: toolbarButtons,
        }}
      />

      <div>
        <Button
          size="large"
          color="primary"
          type="button"
          onClick={onEditFinished}
          disabled={mutationResult.loading}
        >
          {t('COMMON.ABORT')}
        </Button>

        <Button
          size="large"
          color="primary"
          type="button"
          onClick={() => handleEditComment()}
          disabled={mutationResult.loading}
        >
          {t('ALERTS.BTN_SAVE')}
        </Button>
      </div>
    </div>
  );
};

const toolbarButtons = {
  moreText: {
    buttons: [
      'bold',
      'underline',
      'italic',
      'strikeThrough',
      'clearFormatting',
    ],
    buttonsVisible: 5,
  },
  moreParagraph: {
    buttons: [],
    buttonsVisible: 0,
  },
  moreRich: {
    buttons: [],
    buttonsVisible: 0,
  },
  moreMisc: {
    buttons: [],
    buttonsVisible: 0,
  },
};
